import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from 'supabase/database.types'
import {
  CANVAS_FILES_BUCKET,
  CARD_ESTIMATED_HEIGHT,
  CARD_WIDTH,
  FILE_BOUNDS_HEIGHT,
  FILE_BOUNDS_WIDTH,
  GRID_COLS,
  GRID_GAP_X,
  GRID_GAP_Y,
  LINK_BOUNDS_HEIGHT,
  LINK_BOUNDS_WIDTH,
  STAMP_BOUNDS_SIZE,
} from './consts'
import type {
  CanvasBounds,
  CanvasFileItem,
  CanvasLinkItem,
  CanvasStamp,
  CanvasStroke,
  CardPosition,
} from './types'

type LinkPreviewOptions = {
  supabase?: SupabaseClient<Database>
  locale?: string
}

type ProductImageRow = { image_url?: string | null; order?: number | null }
type ProductVariantColorRow = { product_image?: ProductImageRow[] | null }
type ProductVariantRow = {
  product_variant_color?: ProductVariantColorRow[] | null
}
type ProductOpenPreviewRow = {
  item_name?: string | null
  item_name_translations?: string | null
  item_description_translations?: string | null
  product_variant?: ProductVariantRow[] | null
}

export const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

export const createId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

export const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

export const sanitizeCanvasFilename = (name: string) =>
  name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(0, 100)

export const uploadCanvasFile = async (
  supabase: SupabaseClient<Database>,
  quoteId: number,
  file: File
): Promise<string | undefined> => {
  const filePath = `${quoteId}/canvas/${Date.now()}-${sanitizeCanvasFilename(file.name)}`
  const { error } = await supabase.storage
    .from(CANVAS_FILES_BUCKET)
    .upload(filePath, file)
  if (error) {
    console.error('Failed to upload canvas file', error)
    return undefined
  }
  const { data } = supabase.storage
    .from(CANVAS_FILES_BUCKET)
    .getPublicUrl(filePath)
  return data.publicUrl
}

export const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const strokeToPath = (points: CardPosition[]) =>
  points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ')

export const defaultPositionForIndex = (index: number): CardPosition => ({
  x: (index % GRID_COLS) * (CARD_WIDTH + GRID_GAP_X),
  y: Math.floor(index / GRID_COLS) * (CARD_ESTIMATED_HEIGHT + GRID_GAP_Y),
})

export const parseHttpUrl = (text: string): string | null => {
  const trimmed = text.trim()
  if (!trimmed) return null
  try {
    const url = new URL(
      trimmed.startsWith('http://') || trimmed.startsWith('https://')
        ? trimmed
        : `https://${trimmed}`
    )
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return url.href
    }
  } catch {
    // Not a valid URL
  }
  return null
}

export const linkHostname = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

export const linkFaviconUrl = (url: string) => {
  const hostname = linkHostname(url)
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=64`
}

export type LinkPreviewMetadata = {
  title?: string
  description?: string
  imageUrl?: string
}

export const resolveTranslation = (
  translations: string | null | undefined,
  locale: string
) => {
  if (!translations) return ''
  if (translations.includes('||')) {
    const [en, sv] = translations.split('||').map((part) => part.trim())
    return locale === 'sv' ? sv || en : en
  }
  return translations
}

export const parseKlayderProductId = (url: string): number | null => {
  try {
    const parsed = new URL(url)
    const hostname = parsed.hostname.replace(/^www\./, '')
    if (!hostname.endsWith('klayder.com')) return null

    const match = parsed.pathname.match(/^\/product\/(\d+)\/?$/)
    return match ? Number(match[1]) : null
  } catch {
    return null
  }
}

const firstProductImageUrl = (product: ProductOpenPreviewRow) => {
  const variantColors =
    product.product_variant?.[0]?.product_variant_color ?? []
  const images = variantColors[0]?.product_image ?? []
  return (
    [...images].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0]?.image_url ??
    undefined
  )
}

export const fetchKlayderProductPreview = async (
  supabase: SupabaseClient<Database>,
  productId: number,
  locale: string
): Promise<LinkPreviewMetadata | null> => {
  // @ts-ignore - product_open view is not in generated types yet
  const { data, error } = await supabase
    // @ts-ignore - product_open view is not in generated types yet
    .from('product_open')
    .select(
      `item_name,
        item_name_translations,
        item_description_translations,
        product_variant(product_variant_color(product_image(image_url, order)))`
    )
    .eq('id', productId)
    .or('is_draft.is.null,is_draft.eq.false')
    .maybeSingle()

  if (error || !data) return null

  const product = data as ProductOpenPreviewRow
  const productName =
    resolveTranslation(product.item_name_translations, locale) ||
    product.item_name?.trim() ||
    ''

  if (!productName) return null

  const description =
    resolveTranslation(product.item_description_translations, locale) ||
    undefined

  return {
    title: `${productName} | Klayder`,
    description,
    imageUrl: firstProductImageUrl(product),
  }
}

/** Best-effort unfurl for pasted links — falls back to hostname-only preview. */
export const fetchLinkPreview = async (
  url: string,
  options?: LinkPreviewOptions
): Promise<LinkPreviewMetadata> => {
  const productId = parseKlayderProductId(url)
  if (productId && options?.supabase) {
    const klayderPreview = await fetchKlayderProductPreview(
      options.supabase,
      productId,
      options.locale ?? 'en'
    )
    if (klayderPreview) return klayderPreview
  }

  try {
    const res = await fetch(
      `https://api.microlink.io?url=${encodeURIComponent(url)}`
    )
    if (!res.ok) return {}
    const json = (await res.json()) as {
      status?: string
      data?: {
        title?: string
        description?: string
        image?: { url?: string }
      }
    }
    if (json.status !== 'success' || !json.data) return {}
    return {
      title: json.data.title,
      description: json.data.description,
      imageUrl: json.data.image?.url,
    }
  } catch {
    return {}
  }
}

export const readClipboardPaste = async (): Promise<
  | { type: 'image'; file: File }
  | { type: 'link'; url: string }
  | null
> => {
  if (!navigator.clipboard?.read) {
    const text = await navigator.clipboard.readText().catch(() => '')
    const url = parseHttpUrl(text)
    return url ? { type: 'link', url } : null
  }

  try {
    const items = await navigator.clipboard.read()
    for (const item of items) {
      for (const type of item.types) {
        if (type.startsWith('image/')) {
          const blob = await item.getType(type)
          const ext = type.split('/')[1] || 'png'
          return {
            type: 'image',
            file: new File([blob], `pasted-image.${ext}`, { type }),
          }
        }
      }
    }
  } catch {
    // Clipboard read may require permission — fall through to text
  }

  const text = await navigator.clipboard.readText().catch(() => '')
  const url = parseHttpUrl(text)
  return url ? { type: 'link', url } : null
}

export const getCardsBounds = (
  ids: number[],
  positions: Record<number, CardPosition>
): CanvasBounds | null => {
  const placed = ids
    .map((id) => positions[id])
    .filter((pos): pos is CardPosition => pos != null)
  if (!placed.length) return null

  return placed.reduce(
    (bounds, pos) => ({
      minX: Math.min(bounds.minX, pos.x),
      minY: Math.min(bounds.minY, pos.y),
      maxX: Math.max(bounds.maxX, pos.x + CARD_WIDTH),
      maxY: Math.max(bounds.maxY, pos.y + CARD_ESTIMATED_HEIGHT),
    }),
    {
      minX: Infinity,
      minY: Infinity,
      maxX: -Infinity,
      maxY: -Infinity,
    }
  )
}

export type WorldRect = {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

export const cardSelectionId = (quoteSubItemId: number) =>
  `card:${quoteSubItemId}`
export const fileSelectionId = (id: string) => `file:${id}`
export const linkSelectionId = (id: string) => `link:${id}`
export const stampSelectionId = (id: string) => `stamp:${id}`
export const strokeSelectionId = (id: string) => `stroke:${id}`

export const normalizeWorldRect = (
  a: CardPosition,
  b: CardPosition
): WorldRect => ({
  minX: Math.min(a.x, b.x),
  minY: Math.min(a.y, b.y),
  maxX: Math.max(a.x, b.x),
  maxY: Math.max(a.y, b.y),
})

export const rectsIntersect = (a: WorldRect, b: WorldRect) =>
  a.minX <= b.maxX &&
  a.maxX >= b.minX &&
  a.minY <= b.maxY &&
  a.maxY >= b.minY

const xywh = (x: number, y: number, w: number, h: number): WorldRect => ({
  minX: x,
  minY: y,
  maxX: x + w,
  maxY: y + h,
})

export const collectMarqueeSelection = ({
  rect,
  designs,
  positions,
  files,
  links,
  stamps,
  strokes,
}: {
  rect: WorldRect
  designs: Array<{ quoteSubItemId: number }>
  positions: Record<number, CardPosition>
  files: CanvasFileItem[]
  links: CanvasLinkItem[]
  stamps: CanvasStamp[]
  strokes: CanvasStroke[]
}): string[] => {
  const hits: string[] = []

  designs.forEach((item, index) => {
    const pos =
      positions[item.quoteSubItemId] ?? defaultPositionForIndex(index)
    if (
      rectsIntersect(
        rect,
        xywh(pos.x, pos.y, CARD_WIDTH, CARD_ESTIMATED_HEIGHT)
      )
    ) {
      hits.push(cardSelectionId(item.quoteSubItemId))
    }
  })

  files.forEach((file) => {
    if (
      rectsIntersect(
        rect,
        xywh(file.x, file.y, FILE_BOUNDS_WIDTH, FILE_BOUNDS_HEIGHT)
      )
    ) {
      hits.push(fileSelectionId(file.id))
    }
  })

  links.forEach((link) => {
    if (
      rectsIntersect(
        rect,
        xywh(link.x, link.y, LINK_BOUNDS_WIDTH, LINK_BOUNDS_HEIGHT)
      )
    ) {
      hits.push(linkSelectionId(link.id))
    }
  })

  stamps.forEach((stamp) => {
    const half = STAMP_BOUNDS_SIZE / 2
    if (
      rectsIntersect(
        rect,
        xywh(stamp.x - half, stamp.y - half, STAMP_BOUNDS_SIZE, STAMP_BOUNDS_SIZE)
      )
    ) {
      hits.push(stampSelectionId(stamp.id))
    }
  })

  strokes.forEach((stroke) => {
    if (stroke.points.length < 2) return
    const xs = stroke.points.map((p) => p.x)
    const ys = stroke.points.map((p) => p.y)
    const strokeRect: WorldRect = {
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys),
    }
    if (rectsIntersect(rect, strokeRect)) {
      hits.push(strokeSelectionId(stroke.id))
    }
  })

  return hits
}

export const toggleSelectionId = (selected: Set<string>, id: string) => {
  const next = new Set(selected)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

export const selectionBounds = (
  selectedIds: Set<string>,
  designs: Array<{ quoteSubItemId: number }>,
  positions: Record<number, CardPosition>,
  files: CanvasFileItem[],
  links: CanvasLinkItem[],
  stamps: CanvasStamp[],
  strokes: CanvasStroke[] = []
): WorldRect | null => {
  let bounds: WorldRect | null = null
  const expand = (rect: WorldRect) => {
    if (!bounds) {
      bounds = { ...rect }
      return
    }
    bounds = {
      minX: Math.min(bounds.minX, rect.minX),
      minY: Math.min(bounds.minY, rect.minY),
      maxX: Math.max(bounds.maxX, rect.maxX),
      maxY: Math.max(bounds.maxY, rect.maxY),
    }
  }

  designs.forEach((item, index) => {
    const id = cardSelectionId(item.quoteSubItemId)
    if (!selectedIds.has(id)) return
    const pos =
      positions[item.quoteSubItemId] ?? defaultPositionForIndex(index)
    expand(xywh(pos.x, pos.y, CARD_WIDTH, CARD_ESTIMATED_HEIGHT))
  })
  files.forEach((file) => {
    if (!selectedIds.has(fileSelectionId(file.id))) return
    expand(xywh(file.x, file.y, FILE_BOUNDS_WIDTH, FILE_BOUNDS_HEIGHT))
  })
  links.forEach((link) => {
    if (!selectedIds.has(linkSelectionId(link.id))) return
    expand(xywh(link.x, link.y, LINK_BOUNDS_WIDTH, LINK_BOUNDS_HEIGHT))
  })
  stamps.forEach((stamp) => {
    if (!selectedIds.has(stampSelectionId(stamp.id))) return
    const half = STAMP_BOUNDS_SIZE / 2
    expand(
      xywh(stamp.x - half, stamp.y - half, STAMP_BOUNDS_SIZE, STAMP_BOUNDS_SIZE)
    )
  })
  strokes.forEach((stroke) => {
    if (!selectedIds.has(strokeSelectionId(stroke.id))) return
    if (stroke.points.length < 2) return
    const xs = stroke.points.map((p) => p.x)
    const ys = stroke.points.map((p) => p.y)
    expand({
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys),
    })
  })

  return bounds
}
